<template>
	<v-card tile elevation="0" style="height: 100%">
		<v-card-title class="card-header text-left fixed-card-title2">
			<v-row no-gutters>
				<v-col class="align-center d-flex" cols="10">
					<v-toolbar-title>{{ $t('add-schedule') }}</v-toolbar-title>
				</v-col>
				<v-spacer />
				<v-icon color="white" @click="closeDrawer">mdi-close</v-icon>
			</v-row>
		</v-card-title>

		<v-card-text class="overflow-y-auto pt-4" style="height: calc(100vh - 158px)" v-if="scheduleMain">
			<v-form ref="scheduleForm" v-model="formValid" lazy-validation>
				<v-row dense>
					<v-col cols="12">
						<!-- Schedule info -->
						<v-expansion-panels v-model="expandedRRules">
							<v-expansion-panel>
								<v-expansion-panel-header class="pa-4">
									<template #actions>
										<v-icon color="primary">mdi-menu-down</v-icon>
									</template>
									{{ $t('schedule-info') }}
								</v-expansion-panel-header>
								<v-expansion-panel-content class="low-padding-expansion-panel pa-0">
									<v-row dense>
										<!-- Locations/Hospitals -->
										<v-col cols="12" class="text-left">
											<span>{{ $t('select-locations') }}</span>
											<v-data-table
												v-if="hospitals?.length > 0 && scheduleMain"
												:headers="headersHospitals"
												height="300"
												:items="hospitals"
												show-select
												dense
												:loading="loadingHospitals"
												v-model="scheduleMain.scheduleLocations"
												:items-per-page="-1"
												hide-default-footer
												fixed-header
												class="rounded-lg elevation-2">
												<template #[`header.data-table-select`]>
													<v-simple-checkbox
														:indeterminate="
															scheduleMain.scheduleLocations.length > 0 &&
															scheduleMain.scheduleLocations.length < hospitals.length
														"
														:value="scheduleMain.scheduleLocations.length === hospitals.length"
														@click="
															scheduleMain.scheduleLocations =
																scheduleMain.scheduleLocations.length === hospitals.length ? [] : hospitals.slice()
														"
														color="primary" />
												</template>
												<template #[`item.data-table-select`]="{ select, isSelected }">
													<v-simple-checkbox :value="isSelected" @click="select(!isSelected)" color="primary" />
												</template>
												<template #[`item.hospitalName`]="{ item }">
													{{ item.hospitalName }}
												</template>
											</v-data-table>
											<span
												class="color-primary d-flex px-3 font-size12"
												v-if="scheduleMain.scheduleLocations && scheduleMain.scheduleLocations.length === 0">
												{{ $t('at-least-one-location-should-be-selected') }}
											</span>
										</v-col>
										<!-- Schedule Owner -->
										<v-col class="ma-0 d-flex flex-column text-left" cols="12">
											<span>{{ $t('select-staff-and-coordinator') }}</span>
											<v-radio-group
												class="mt-0"
												hide-details
												v-model="scheduleMain.scheduleOwner"
												:rules="[(v) => !!v || $t('required-field')]">
												<v-data-table
													:headers="headersOwnersAndStaff"
													:items="medicalStaffAbleToDoBillingForOrg"
													height="300"
													show-select
													outlined
													dense
													v-model="scheduleMain.scheduleExecutors"
													class="rounded-lg elevation-2"
													:items-per-page="-1"
													hide-default-footer
													fixed-header>
													<template #[`header.data-table-select`]>
														<v-simple-checkbox
															:indeterminate="
																scheduleMain.scheduleExecutors.length > 0 &&
																scheduleMain.scheduleExecutors.length < medicalStaffAbleToDoBillingForOrg.length
															"
															:value="
																scheduleMain.scheduleExecutors.length === medicalStaffAbleToDoBillingForOrg.length
															"
															@click="
																scheduleMain.scheduleExecutors =
																	scheduleMain.scheduleExecutors.length === medicalStaffAbleToDoBillingForOrg.length
																		? []
																		: medicalStaffAbleToDoBillingForOrg.slice()
															"
															color="primary" />
													</template>
													<template #[`item.data-table-select`]="{ select, isSelected }">
														<v-simple-checkbox :value="isSelected" @click="select(!isSelected)" color="primary" />
													</template>
													<template #[`item.firstName`]="{ item }">{{ item.firstName }} {{ item.lastName }}</template>
													<template #[`item.scheduleOwner`]="{ item }">
														<div class="d-flex justify-end">
															<v-radio hide-details :value="item.id" color="primary" />
														</div>
													</template>
												</v-data-table>
											</v-radio-group>
											<div class="color-primary d-flex px-3 font-size12" v-if="!this.scheduleMain.scheduleOwner">
												{{ $t('coordinator-is-required') }}
											</div>
											<div
												class="color-primary d-flex px-3 font-size12"
												v-if="this.scheduleMain.scheduleExecutors && this.scheduleMain.scheduleExecutors.length === 0">
												{{ $t('at-least-one-staff-member-should-be-selected') }}
											</div>
										</v-col>
										<!-- Schedule rrule name -->
										<v-col cols="12">
											<v-text-field
												type="text"
												v-model="scheduleMain.scheduleRuleName"
												outlined
												dense
												:label="$t('name')"
												clearable
												hide-details="auto"
												:rules="[(v) => !!v || $t('required-field')]"
												validate-on-blur />
										</v-col>
										<!-- Schedule rrule description -->
										<v-col cols="12">
											<v-textarea
												type="text"
												v-model="scheduleMain.scheduleRuleDescription"
												outlined
												hide-details
												dense
												:label="$t('description')"
												rows="3"
												clearable />
										</v-col>
										<!-- Start & End date time -->
										<v-col cols="12">
											<v-sheet border="error xl" class="mx-auto">
												<span class="d-flex w-100 text-left flex-row align-center">
													{{ $t('start-&-end-date') }}
													<Tooltip :text="$t('rrule-explanation-start-end-date-time')">
														<v-icon color="grey">mdi-help-circle-outline</v-icon>
													</Tooltip>
												</span>
												<v-tabs v-model="scheduleInfo.dateCreationOption" bg-color="primary" class="mb-2" grow>
													<v-tab
														value="month"
														:class="{
															'selected-tab': scheduleInfo.dateCreationOption === 0,
															'unselected-tab': scheduleInfo.dateCreationOption !== 0,
														}">
														{{ $t('month') }}
													</v-tab>
													<v-tab
														value="custom"
														:class="{
															'selected-tab': scheduleInfo.dateCreationOption === 1,
															'unselected-tab': scheduleInfo.dateCreationOption !== 1,
														}">
														{{ $t('custom') }}
													</v-tab>
												</v-tabs>
												<div v-if="scheduleInfo.dateCreationOption === 0">
													<v-row dense>
														<v-col cols="12">
															<v-select
																attach
																:menu-props="{ offsetY: true }"
																v-model="scheduleInfo.month"
																:items="monthOptions"
																:label="$t('month')"
																outlined
																hide-details
																dense
																class="text-capitalize" />
														</v-col>
														<v-col cols="12">
															<v-text-field
																v-model.number="scheduleInfo.year"
																type="number"
																:label="$t('year')"
																outlined
																dense
																@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
																hide-details="auto"
																:rules="[(v) => !!v || $t('required-field')]" />
														</v-col>
													</v-row>
												</div>
												<div v-else-if="scheduleInfo.dateCreationOption === 1">
													<v-row dense>
														<v-col cols="6">
															<DateTimePicker
																:minDate="currentDate"
																v-model="scheduleInfo.selectedStartDateTime"
																:label="$t('select-start-date-time')"
																:valid.sync="isValidSelectedStartDateTime"
																:errorMessage="!isValidSelectedStartDateTime ? $t('required-field') : ''" />
														</v-col>
														<v-col cols="6">
															<DateTimePicker
																:minDate="scheduleInfo.selectedStartDateTime"
																v-model="scheduleInfo.selectedEndDateTime"
																:label="$t('select-end-date-time')"
																:valid.sync="isValidSelectedEndDateTime"
																:disabled="scheduleInfo.selectedStartDateTime === null" />
														</v-col>
													</v-row>
												</div>
											</v-sheet>
										</v-col>
									</v-row>
									<span class="color-primary" v-if="occurrencesGenerationProblem.length">
										{{ occurrencesGenerationProblem }}
									</span>
								</v-expansion-panel-content>
							</v-expansion-panel>
							<v-expansion-panel
								v-for="(tasksOccurrence, indexSchedule) in scheduleMain.scheduleOccurrencesCreateDto"
								:key="indexSchedule">
								<v-expansion-panel-header class="pa-4">
									<template #actions>
										<v-icon color="primary">mdi-menu-down</v-icon>
									</template>
									<div>
										<v-icon
											v-if="scheduleMain.scheduleOccurrencesCreateDto.length > 1"
											color="primary"
											@click.stop="deleteTaskOccurrence(indexSchedule)">
											mdi-delete
										</v-icon>
										{{ $t('task-list') }}: {{ tasksOccurrence.occurrenceName }}
									</div>
								</v-expansion-panel-header>
								<v-expansion-panel-content class="low-padding-expansion-panel">
									<v-row dense>
										<!-- Occurrence rrule name -->
										<v-col cols="12">
											<v-text-field
												type="text"
												v-model="tasksOccurrence.occurrenceName"
												outlined
												dense
												:label="$t('name')"
												clearable
												hide-details="auto"
												:rules="[(v) => !!v || $t('required-field')]"
												validate-on-blur />
										</v-col>
										<!-- Occurrence rrule description -->
										<v-col cols="12">
											<v-textarea
												type="text"
												v-model="tasksOccurrence.occurrenceDescription"
												outlined
												dense
												hide-details
												:label="$t('description')"
												rows="3"
												clearable />
										</v-col>
										<!-- Occurrence rrule frequency -->
										<v-col class="ma-0 pr-0 d-flex flex-row align-baseline" cols="4">
											<v-select
												attach
												class="pr-1"
												v-model="tasksOccurrence.frequency"
												:items="freqOptions"
												:label="$t('frequency')"
												outlined
												hide-details
												dense
												:menu-props="{ offsetY: true }"
												:disabled="tasksOccurrence.byWeekDay.length + tasksOccurrence.byMonthDay.length > 0" />
											<Tooltip :text="$t('rrule-explanation-frequency')">
												<v-icon color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
										</v-col>
										<!-- Occurrence rrule interval -->
										<v-col class="ma-0 pr-0 d-flex flex-row align-baseline" cols="4">
											<v-text-field
												class="pr-1"
												v-model.number="tasksOccurrence.interval"
												type="number"
												:label="$t('interval')"
												outlined
												hide-details
												dense
												@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
												min="1" />
											<Tooltip :text="$t('rrule-explanation-interval')">
												<v-icon color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
										</v-col>
										<!-- Occurrence rrule count -->
										<v-col class="ma-0 d-flex flex-row align-baseline" cols="4">
											<v-text-field
												class="pr-1"
												v-model.number="tasksOccurrence.count"
												type="number"
												:label="$t('count')"
												outlined
												@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
												dense
												hide-details="auto"
												:error="
													scheduleInfo.dateCreationOption === 1 &&
													!scheduleInfo.selectedEndDateTime &&
													(tasksOccurrence.count == null || tasksOccurrence.count === '' || tasksOccurrence.count === 0)
												"
												:error-messages="
													scheduleInfo.dateCreationOption === 1 &&
													!scheduleInfo.selectedEndDateTime &&
													(tasksOccurrence.count == null || tasksOccurrence.count === '' || tasksOccurrence.count === 0)
														? $t('required-field')
														: ''
												" />
											<Tooltip :text="$t('rrule-explanation-count')">
												<v-icon color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
										</v-col>

										<!-- DEFAULT / FALLBACK TIME -->
										<v-col cols="4" class="pt-2">
											<v-text-field
												v-model="tasksOccurrence.defaultTime"
												type="time"
												:label="$t('start-time')"
												outlined
												dense
												hide-details
												clearable
												:disabled="tasksOccurrence.byHour.length > 0 || tasksOccurrence.byMinute.length > 0" />
										</v-col>

										<!-- Task Duration -->
										<v-col class="ma-0 d-flex flex-row align-center pt-2" cols="8">
											<v-text-field
												:disabled="isAllDayTask"
												v-model.number="tasksOccurrence.occurrenceDuration"
												class="pr-1"
												type="number"
												:label="$t('planned-duration')"
												@keypress="(e) => /[0-9]/.test(e.key) || e.preventDefault()"
												outlined
												dense
												hide-details="auto"
												:rules="[
													(v) => v > 0 || $t('value-should-be-greater-than-zero'),
													(v) => v <= 1439 || $t('value-should-be-less-than-1440'),
												]"
												validate-on-blur
												min="1"
												max="1439" />
											<Tooltip :text="$t('rrule-explanation-duration-minutes')">
												<v-icon size="20" color="grey">mdi-help-circle-outline</v-icon>
											</Tooltip>
											<v-switch
												color="primary"
												:label="$t('all-day') + '?'"
												:value="tasksOccurrence.occurrenceDuration >= 1439"
												@change="onIsAllDayTaskChange($event, indexSchedule)"
												hide-details
												class="pl-2 ma-0" />
										</v-col>
										<!-- Expandable Advanced Options -->
										<v-col cols="12">
											<v-expansion-panels v-model="expandedAdvancedOptions" style="height: calc(100% - 56px)" elevation>
												<v-expansion-panel class="ma-0">
													<v-expansion-panel-header class="pa-2">
														<template #actions>
															<v-icon color="primary">mdi-menu-down</v-icon>
														</template>
														{{ $t('advanced-options') }}
													</v-expansion-panel-header>
													<v-expansion-panel-content class="low-padding-expansion-panel pa-0">
														<v-row dense>
															<!-- BY WEEKDAY -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byWeekDay"
																	:items="weekdayOptions"
																	:label="$t('by-weekday')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<span v-if="index === 0">
																			{{ item.text }}
																		</span>
																		<span
																			v-if="index === 1 && tasksOccurrence.byWeekDay.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byWeekDay.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-day')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>
															<!-- BY MONTH DAY -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byMonthDay"
																	:items="monthDayOptions"
																	:label="$t('by-date')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<v-chip v-if="index === 0" small>
																			{{ item.text }}
																		</v-chip>
																		<span
																			v-if="index === 1 && tasksOccurrence.byMonthDay.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byMonthDay.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-date')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>
															<!-- BY HOUR -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byHour"
																	:items="hourOptions"
																	:label="$t('by-hour')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<v-chip v-if="index === 0" small>
																			{{ item.text }}
																		</v-chip>
																		<span
																			v-if="index === 1 && tasksOccurrence.byHour.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byHour.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-hour')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>
															<!-- BY MINUTE -->
															<v-col class="ma-0 d-flex flex-row align-baseline" cols="12">
																<v-select
																	attach
																	v-model="tasksOccurrence.byMinute"
																	:items="minuteOptions"
																	:label="$t('by-minute')"
																	multiple
																	outlined
																	hide-details
																	dense
																	clearable
																	:menu-props="{ offsetY: true }">
																	<template #selection="{ item, index }">
																		<v-chip v-if="index === 0" small>
																			{{ item.text }}
																		</v-chip>
																		<span
																			v-if="index === 1 && tasksOccurrence.byMinute.length > 1"
																			class="grey--text text-caption">
																			&nbsp;(+{{ tasksOccurrence.byMinute.length - 1 }} {{ $t('more') }})
																		</span>
																	</template>
																</v-select>
																<Tooltip :text="$t('rrule-explanation-by-minute')">
																	<v-icon color="grey">mdi-help-circle-outline</v-icon>
																</Tooltip>
															</v-col>

															<!-- <v-col cols="12">
																<FileUpload
																	@file-selected="onFileSelected"
																	@uploaded="onFileUploaded"
																	@error="onFileError" />
															</v-col> -->
														</v-row>
													</v-expansion-panel-content>
												</v-expansion-panel>
											</v-expansion-panels>
										</v-col>
									</v-row>
								</v-expansion-panel-content>
							</v-expansion-panel>
						</v-expansion-panels>
					</v-col>
					<v-col cols="12">
						<v-btn block outlined color="primary" @click="addNewRRule()">+ {{ $t('add-new-task-list-item') }}</v-btn>
					</v-col>
				</v-row>
			</v-form>
		</v-card-text>
		<v-container class="px-4" v-if="scheduleMain">
			<v-row dense>
				<v-col cols="12" order="1">
					<v-btn block small outlined color="primary" :disabled="!canSaveRRule" @click="previewSchedule">
						{{ $t('click-to-preview-events') }}
					</v-btn>
				</v-col>
				<v-col cols="12" order="2">
					<v-btn block color="primary" :disabled="!canSaveRRule" @click="saveRRule">{{ $t('save') }}</v-btn>
				</v-col>
			</v-row>
		</v-container>
		<v-dialog v-if="showDialog" persistent v-model="showDialog" min-width="600px" width="50vw">
			<v-card class="pa-2">
				<v-card-title class="text-capitalize">
					{{ $t('preview') }} {{ $t('events') }}
					<v-spacer />
					<v-icon color="black" @click="showDialog = false">mdi-close</v-icon>
				</v-card-title>
				<v-card-text>
					<div class="d-flex flex-row flex-wrap justify-space-between align-center">
						<div class="d-flex">
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'month'"
								class="white--text"
								:color="calendarView === 'month' ? 'primary' : 'primary2'"
								style="border-radius: 8px 0 0 8px">
								{{ $t('month') }}
							</v-btn>
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'week'"
								class="white--text"
								:class="calendarView === 'week' ? 'primaryBg' : 'primary2Bg'"
								style="border-left: 1px solid #000; border-right: 1px solid #000">
								{{ $t('week') }}
							</v-btn>
							<v-btn
								elevation="0"
								tile
								@click="calendarView = 'day'"
								:color="calendarView === 'day' ? 'primary' : 'primary2'"
								class="white--text"
								style="border-radius: 0 8px 8px 0">
								{{ $t('day') }}
							</v-btn>
						</div>
						<h3>{{ getMonthName(currentMonth) }} {{ currentYear }}</h3>
						<div class="my-2">
							<v-btn color="primary" @click="goPrev"><v-icon>mdi-arrow-left</v-icon>{{ $t('prev') }}</v-btn>
							<v-btn color="primary" @click="goToday" class="white--text">
								{{ $t('today') }}
							</v-btn>
							<v-btn color="primary" @click="goNext">{{ $t('next') }}<v-icon>mdi-arrow-right</v-icon></v-btn>
						</div>
					</div>
					<v-sheet height="600">
						<v-calendar
							ref="calendar"
							v-model="currentDate"
							:type="calendarView"
							:events="calendarEvents"
							:event-color="(e) => e.color"
							@click:more="viewDate"
							@click:date="viewDate"
							:weekdays="[1, 2, 3, 4, 5, 6, 0]">
							<template #event="{ event }">
								<v-tooltip
									v-if="event.description"
									:color="!event.isSuccess && !event.isError ? 'light' : 'primary'"
									top>
									<template #activator="{ on }">
										<div
											class="d-flex align-center justify-center black--text overflow-hidden"
											style="height: 100%"
											v-on="on">
											{{ event.name }}
											<span v-if="event.isSuccess">
												<v-icon class="outlined-icon" color="success" size="small"> mdi-check-circle </v-icon>
											</span>
											<span v-else-if="event.isError">
												<v-icon class="outlined-icon" color="primary" size="small"> mdi-close-circle </v-icon>
											</span>
										</div>
									</template>
									<span>{{ event.description }}</span>
								</v-tooltip>
								<div v-else class="d-flex align-center justify-center black--text overflow-hidden" style="height: 100%">
									{{ event.name }}
									<span v-if="event.isSuccess">
										<v-icon class="outlined-icon" color="success" size="small"> mdi-check-circle </v-icon>
									</span>
									<span v-else-if="event.isError">
										<v-icon class="outlined-icon" color="primary" size="small"> mdi-close-circle </v-icon>
									</span>
								</div>
							</template>

							<template #day-label-header="{ day, date }">
								<div class="text-center">
									<div>{{ day }} {{ monthName(date) }}</div>
								</div>
							</template>
						</v-calendar>
					</v-sheet>
				</v-card-text>
			</v-card>
		</v-dialog>
		<div v-if="!scheduleMain" class="d-flex flex-column align-center justify-center" style="height: calc(100vh - 62px)">
			<!-- Spinner -->
			<v-progress-circular indeterminate color="primary" size="64" width="6" />
			<!-- Loading Text -->
			<span class="mt-4 text-h6">{{ $t('loading') }}</span>
		</div>
	</v-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import { RRule, RRuleSet } from 'rrule';
import DateTimePicker from '../Client/components/DateTimePicker.vue';
// import FileUpload from './FileUpload.vue';
import {
	getBrowserTimeZone,
	getHospitals,
	getMonthName,
	monthName,
	months,
	showErrorAlert,
	showSuccessAlert,
} from '../../utils/utilities';
import Tooltip from '../Client/components/Tooltip.vue';

export default {
	components: { DateTimePicker, Tooltip },
	data () {
		return {
			formValid: false,
			loadingHospitals: false,
			expandedAdvancedOptions: [],
			scheduleMain: null,
			scheduleMainInitial: {
				scheduleCreatedAtOrganizationId: 0, // number
				scheduleCreatedAtLocationId: 0, // location Id where the rrule is created from
				scheduleLocations: [], // the locations IDS where the rrule will be applied

				scheduleRule: '', // string (RRULE / cron)
				scheduleRuleName: '', // string
				scheduleRuleDescription: '', // string

				scheduleStartDate: new Date(), // Date (ISO string or null)
				scheduleEndDate: null, // Date (nullable)
				scheduleCreatedBy: 0, // number (user ID)
				scheduleOwner: 0, // number (user ID)
				scheduleCreatedAt: new Date().toISOString(), // default now
				scheduleExecutors: [],
				scheduleTimezoneId: 'UTC', // string (timezone)

				additionalData: '', // string (JSON or text)

				scheduleFileName: '', // string
				scheduleFileType: '', // string
				scheduleFileExtension: '', // string
				scheduleFileData: null, // base64 or ArrayBuffer if needed
				scheduleOccurrencesCreateDto: [],
			},
			rruleNew: null,
			rruleNewInitial: {
				occurrenceName: '',
				occurrenceDescription: '',
				occurrenceStartAt: '', // rrule.dtstart
				occurrenceEndAt: null, // rrule.until
				rruleString: '',
				occurrenceDuration: 60,
				frequency: RRule.DAILY, // rrule.freq
				interval: null, // rrule.interval
				count: null, // rrule.count
				byWeekDay: [], // rrule.byWeekDay
				startDayOfWeek: RRule.MO, // rrule.wkst
				byMonth: [], // rrule.bymonth
				byMonthDay: [], // rrule.bymonthday
				byYearDay: [], // rrule.byyearday
				byWeekNumber: [], // rrule.byweekno
				byHour: [], // rrule.byhour
				byMinute: [], // rrule.byminute
				defaultTime: '00:00', // fallback time string 'HH:MM' used when byHour/byMinute not set — default to 12:00 AM
				status: 0,
			},
			scheduleInfo: {
				scheduleByDateOrMonth: 'month', // can be "month" or "date"
				month: new Date().getMonth() + 1,
				year: new Date().getFullYear(),
				dateCreationOption: 0, // 0 = month, 1 = custom
				selectedStartDateTime: null,
				selectedEndDateTime: null,
			},
			calendarEvents: [],
			hourOptions: Array.from({ length: 24 }, (_, i) => ({ text: `${i}:00`, value: i })),
			minuteOptions: Array.from({ length: 12 }, (_, i) => ({ text: `${i * 5}`, value: i * 5 })),
			selectedStartDateTime: '',
			isValidSelectedStartDateTime: false,
			selectedEndDateTime: '',
			isValidSelectedEndDateTime: false,

			calendarView: 'month',
			currentDate: new Date().toISOString().substr(0, 10),
			medicalStaffAbleToDoBillingForOrg: [],
			expandedRRules: 0,
			monthOptions: [],
			frequencyMap: {
				[RRule.YEARLY]: 'YEARLY',
				[RRule.MONTHLY]: 'MONTHLY',
				[RRule.WEEKLY]: 'WEEKLY',
				[RRule.DAILY]: 'DAILY',
				[RRule.HOURLY]: 'HOURLY',
				[RRule.MINUTELY]: 'MINUTELY',
				[RRule.SECONDLY]: 'SECONDLY',
			},
			occurrencesGenerationProblem: '',
			showDialog: false,
			isAllDayTask: false,
			isAdvancedUsed: false,
			currentYear: -1,
			currentMonth: -1,
		};
	},
	mounted () {
		this.currentMonth = new Date(this.currentDate).getMonth() + 1;
		this.currentYear = new Date(this.currentDate).getFullYear();
	},
	computed: {
		...mapState({
			hospitals: (state) => state.hospitals.hospitals,
			userData: (state) => state.authentication.userData,
			relatedMedicalStaff: (state) => state.hospitals.users,
			authenticated: (state) => state.authentication.userData,
		}),
		freqOptions () {
			// | Frequency | RRule Constant | Numeric Value |
			// | --------- | -------------- | ------------- |
			// | YEARLY    | RRule.YEARLY   | 0             |
			// | MONTHLY   | RRule.MONTHLY  | 1             |
			// | WEEKLY    | RRule.WEEKLY   | 2             |
			// | DAILY     | RRule.DAILY    | 3             |
			// | HOURLY    | RRule.HOURLY   | 4             |
			// | MINUTELY  | RRule.MINUTELY | 5             |
			// | SECONDLY  | RRule.SECONDLY | 6             |
			return [
				{ text: this.$t('daily'), value: RRule.DAILY, key: 'DAILY' },
				{ text: this.$t('weekly'), value: RRule.WEEKLY, key: 'WEEKLY' },
				{ text: this.$t('monthly'), value: RRule.MONTHLY, key: 'MONTHLY' },
				{ text: this.$t('yearly'), value: RRule.YEARLY, key: 'YEARLY' },
			];
		},
		monthDayOptions () {
			return [
				...Array.from({ length: 31 }, (_, i) => ({ text: `${i + 1}`, value: i + 1 })),
				{ text: this.$t('last-day-of-the-month'), value: -1 },
			];
		},
		headersHospitals () {
			return [{ text: this.$t('location-name'), value: 'hospitalName' }];
		},
		headersOwnersAndStaff () {
			return [
				{ text: this.$t('staff'), value: 'firstName' },
				{ text: this.$t('schedule-coordinator'), value: 'scheduleOwner', align: 'end' },
			];
		},
		weekdayOptions () {
			const weekdayOptions = [
				{ text: this.$t('monday'), value: RRule.MO },
				{ text: this.$t('tuesday'), value: RRule.TU },
				{ text: this.$t('wednesday'), value: RRule.WE },
				{ text: this.$t('thursday'), value: RRule.TH },
				{ text: this.$t('friday'), value: RRule.FR },
				{ text: this.$t('saturday'), value: RRule.SA },
				{ text: this.$t('sunday'), value: RRule.SU },
			];
			return weekdayOptions;
		},
		canSaveRRule () {
			// 🧩 1. If the main schedule object doesn't exist, saving is not possible.
			if (!this.scheduleMain) return false;

			// ⚠️ 2. Check for a specific validation issue:
			// If the user selected the "Custom Date" option (dateCreationOption === 1)
			// AND a Start DateTime is set,
			// AND occurrences are defined,
			// BUT at least one occurrence has a null 'count' (no repeat count defined),
			// then set an error message (translated) to warn that count is required.
			// Otherwise, clear the error message.
			// eslint-disable-next-line vue/no-side-effects-in-computed-properties
			this.occurrencesGenerationProblem =
				this.scheduleInfo.dateCreationOption === 1 && // using custom start/end times
				this.scheduleInfo.selectedStartDateTime !== null && // start date/time is selected
				this.scheduleInfo.selectedEndDateTime === null && // end date/time is not selected
				this.scheduleMain.scheduleOccurrencesCreateDto && // occurrences exist
				this.scheduleMain.scheduleOccurrencesCreateDto?.some((s) => s.count === null) // at least one count missing
					? this.$t('count-is-required-in-every-occurrence') // set translated warning
					: ''; // otherwise clear the warning

			// ✅ 3. Perform overall validation for the main schedule configuration
			const checkSchedule =
				this.scheduleMain.scheduleLocations.length > 0 && // at least one location selected
				this.scheduleMain.scheduleOwner > 0 && // schedule owner is defined
				this.scheduleMain.scheduleExecutors.length > 0 && // at least one executor assigned
				this.scheduleMain.scheduleRuleName.length > 0 && // schedule name is not empty
				(this.scheduleInfo.dateCreationOption === 0 // if "year-based" schedule option
					? this.scheduleInfo.year.toString().length > 0 // ensure year is defined
					: this.scheduleInfo.selectedStartDateTime !== null && // start date/time selected
					  (this.scheduleInfo.selectedEndDateTime !== null || // end date/time is not selected
							(this.scheduleMain.scheduleOccurrencesCreateDto && // occurrences exist
								this.scheduleMain.scheduleOccurrencesCreateDto.every((s) => s.count !== null && s.count > 0)))); // all counts defined

			// ✅ 4. Validate all individual Tasks/Occurrences in the schedule
			const checkTasksOccurrences =
				this.scheduleMain.scheduleOccurrencesCreateDto && // occurrences exist
				this.scheduleMain.scheduleOccurrencesCreateDto.length > 0 && // not empty
				this.scheduleMain.scheduleOccurrencesCreateDto.reduce((acc, curr) => {
					// ensure that every occurrence:
					return (
						acc &&
						curr.occurrenceName.length > 0 && // has a name
						curr.rruleString.length > 0 && // has a recurrence rule string
						curr.occurrenceDuration > 0 // has a valid duration
					);
				}, true);

			// ✅ 5. Return true only if both the main schedule and all occurrences are valid.
			return checkSchedule && checkTasksOccurrences;
		},
	},
	async created () {
		this.monthOptions = this.months();
		await this.getHospitals();
		await this.initializeNewScheduleCreation();
	},
	methods: {
		getMonthName,
		months,
		getHospitals,
		getBrowserTimeZone,
		monthName,
		...mapActions({
			createNewSchedule: 'schedules/createSchedule',
		}),
		...mapMutations({
			ADD_SCHEDULE: 'schedules/ADD_SCHEDULE',
		}),
		async initializeNewScheduleCreation () {
			this.scheduleMain = JSON.parse(JSON.stringify(this.scheduleMainInitial));
			this.rruleNew = JSON.parse(JSON.stringify(this.rruleNewInitial));
			// Define initial structure of the body
			this.scheduleMain.scheduleCreatedAtOrganizationId = this.userData.organizationId;
			this.scheduleMain.scheduleCreatedAtLocationId = Number(localStorage.getItem('selectedHospitalAssignedId') || -1);
			this.scheduleMain.scheduleCreatedBy = this.userData.id;

			// To list the users to assign the schedule
			await this.getMedicalStaffAbleToDoBillingForOrg();
			await this.addNewRRule();
		},
		deleteTaskOccurrence (indexSchedule) {
			this.scheduleMain.scheduleOccurrencesCreateDto = this.scheduleMain.scheduleOccurrencesCreateDto.filter(
				(t, index) => index !== indexSchedule,
			);
		},
		async addNewRRule () {
			// Reset the tab
			this.scheduleInfo.dateCreationOption = 0;
			// Add an empty rrule in the array
			const rruleToAdd = JSON.parse(JSON.stringify(this.rruleNew));
			this.scheduleMain.scheduleOccurrencesCreateDto = [...this.scheduleMain.scheduleOccurrencesCreateDto, rruleToAdd];

			await this.manageCreationStartEndDatePerMonth();
			this.expandedRRules =
				this.scheduleMain.scheduleOccurrencesCreateDto.length > 1
					? this.scheduleMain.scheduleOccurrencesCreateDto.length
					: 0;
		},
		viewDate ({ date }) {
			this.calendarView = 'day';
			this.currentDate = date;
		},
		async getMedicalStaffAbleToDoBillingForOrg () {
			await this.$store.dispatch('hospitals/getMedicalStaffAbleToDoBillingForOrg').then((res) => {
				if (this.scheduleMain?.scheduleTimezoneId) {
					this.scheduleMain.scheduleTimezoneId = res.data.find((d) => d.id === this.userData.id)?.userTimezone || '';
				}
				this.medicalStaffAbleToDoBillingForOrg = res.data;
			});
		},
		onFileSelected (file) {},
		onFileUploaded (response) {
			showSuccessAlert(this.$t('successfully-created-organization'));
		},
		onFileError (err) {
			showErrorAlert(`${this.$t('error')}: ${err}`);
		},
		getFrequencyKeyByValue (value) {
			return this.frequencyMap[value] || null;
		},
		async saveRRule () {
			// Validate the form before saving
			if (
				!this.$refs.scheduleForm.validate() ||
				(this.scheduleMain.scheduleExecutors && this.scheduleMain.scheduleExecutors.length === 0)
			) {
				this.expandedRRules = 0;
				return;
			}

			const ruleStrings = this.scheduleMain.scheduleOccurrencesCreateDto.map((t) => t.rruleString);
			const rruleSet = new RRuleSet();
			// Add all rules
			ruleStrings.forEach((str) => {
				// const fixedRRule = this.normalizeRRuleToLocal(str);
				const rule = RRule.fromString(str);
				rruleSet.rrule(rule);
			});
			const combinedString = rruleSet.toString();

			this.scheduleMain.scheduleRule = combinedString;
			const scheduleOccurrencesCreateDtoFormatBeforeSave = this.scheduleMain.scheduleOccurrencesCreateDto.map((s) => {
				// exclude transient fields from the payload
				const { interval, count, defaultTime, ...occ } = s;

				// compute byHour/byMinute to send to backend: prefer explicit arrays, otherwise parse defaultTime
				let byHour = s.byHour && s.byHour.length ? s.byHour : [];
				let byMinute = s.byMinute && s.byMinute.length ? s.byMinute : [];
				// If hours are set but minutes are not, default minutes to 0 for saving
				if (byHour.length && !byMinute.length) {
					byMinute = [0];
				}
				if (!byHour.length && !byMinute.length && s.defaultTime) {
					const parts = s.defaultTime.split(':').map((p) => Number(p));
					const h = Number.isFinite(parts[0]) ? parts[0] : null;
					const m = parts.length > 1 && Number.isFinite(parts[1]) ? parts[1] : 0;
					if (h !== null && !Number.isNaN(h)) byHour = [h];
					if (!Number.isNaN(m)) byMinute = [m];
				}

				return {
					...occ,
					// byWeekDay can be stored as objects (with .weekday) or plain numbers — normalize to numbers
					byWeekDay:
						s.byWeekDay && s.byWeekDay.length
							? s.byWeekDay.map((w) => (w && w.weekday !== undefined ? w.weekday : w))
							: [],
					startDayOfWeek:
						s.startDayOfWeek && s.startDayOfWeek.weekday !== undefined ? s.startDayOfWeek.weekday : s.startDayOfWeek,
					frequency: this.getFrequencyKeyByValue(s.frequency),
					...(interval !== '' && Number(interval) > 1 ? { interval: Number(interval) } : {}),
					...(count !== '' && Number(count) > 1 ? { count: Number(count) } : {}),
					...(byHour.length ? { byHour } : {}),
					...(byMinute.length ? { byMinute } : {}),
				};
			});

			const body = {
				scheduleCreatedAtOrganizationId: this.scheduleMain.scheduleCreatedAtOrganizationId, // 0,
				scheduleCreatedAtLocationId: this.scheduleMain.scheduleCreatedAtLocationId, // 0,
				scheduleRule: this.scheduleMain.scheduleRule, // 'string',
				scheduleRuleName: this.scheduleMain.scheduleRuleName, // 'string',
				scheduleRuleDescription: this.scheduleMain.scheduleRuleDescription, // 'string',
				scheduleStartDate: this.scheduleMain.scheduleStartDate,
				scheduleEndDate: this.scheduleMain.scheduleEndDate,
				scheduleCreatedBy: this.scheduleMain.scheduleCreatedBy, // 0,
				scheduleOwner: this.scheduleMain.scheduleOwner, // 0,
				scheduleCreatedAt: this.scheduleMain.scheduleCreatedAt, // '2025-10-08T06:58:20.930Z',
				scheduleTimezoneId: this.getBrowserTimeZone(), // this.userData.userTimezone,
				scheduleLocations: this.scheduleMain.scheduleLocations.map((l) => l.id),
				scheduleExecutors: this.scheduleMain.scheduleExecutors.map((l) => l.id),
				scheduleOccurrencesCreateDto: scheduleOccurrencesCreateDtoFormatBeforeSave,
			};

			this.createNewSchedule(body).then((res) => {
				if (res.data.resFlag) {
					this.initializeNewScheduleCreation();
					const selectedHospitalAssignedId = JSON.parse(localStorage.getItem('selectedHospitalAssignedId'));
					this.$store.dispatch('schedules/getSchedulesByLocationId', selectedHospitalAssignedId);
					this.closeDrawer();
				} else {
					this.showErrorAlert(res.data.msg);
				}
			});
		},

		async generateEventsFromRRuleStrings (rruleList = this.scheduleMain.scheduleOccurrencesCreateDto || []) {
			this.calendarEvents = [];
			rruleList.forEach((rruleItem) => {
				const occurs = RRule.fromString(rruleItem.rruleString.replace(' ', '\n')).all();
				occurs.forEach((d) => {
					const date = new Date(d.getTime() + d.getTimezoneOffset() * 60000);
					this.calendarEvents.push({
						name: rruleItem.occurrenceName || '',
						description: rruleItem.occurrenceDescription || '',
						start: date,
						end: new Date(date.getTime() + Number(rruleItem.occurrenceDuration) * 60000),
						timed: true,
						color: 'blue',
					});
				});
			});
		},
		// Calendar methods
		goPrev () {
			const base = new Date(this.currentDate);
			if (this.calendarView === 'month') base.setMonth(base.getMonth() - 1);
			else if (this.calendarView === 'week') base.setDate(base.getDate() - 7);
			else base.setDate(base.getDate() - 1);
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
		goNext () {
			const base = new Date(this.currentDate);
			if (this.calendarView === 'month') base.setMonth(base.getMonth() + 1);
			else if (this.calendarView === 'week') base.setDate(base.getDate() + 7);
			else base.setDate(base.getDate() + 1);
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},

		manageCreationStartEndDatePerMonth (newVal = this.scheduleInfo) {
			if (newVal.dateCreationOption === 0) {
				const startDate = new Date(newVal.year, newVal.month - 1, 1, 0, 0, 0);
				const endDate = new Date(newVal.year, newVal.month, 0, 23, 59, 59);
				this.scheduleMain.scheduleStartDate = new Date(startDate.getTime() - startDate.getTimezoneOffset() * 60000);
				this.scheduleMain.scheduleEndDate = new Date(endDate.getTime() - endDate.getTimezoneOffset() * 60000);
			} else {
				this.scheduleMain.scheduleStartDate = new Date(newVal.selectedStartDateTime);
				if (newVal.selectedEndDateTime) {
					this.scheduleMain.scheduleEndDate = new Date(newVal.selectedEndDateTime);
				}
			}
			for (const tasksOccurrence of this.scheduleMain.scheduleOccurrencesCreateDto) {
				tasksOccurrence.occurrenceStartAt = this.scheduleMain.scheduleStartDate;
				if (this.scheduleMain.scheduleEndDate !== null) {
					tasksOccurrence.occurrenceEndAt = this.scheduleMain.scheduleEndDate;
				}
			}
		},
		previewSchedule () {
			this.generateEventsFromRRuleStrings().then(() => {
				this.showDialog = true;
			});
		},

		closeDrawer () {
			// this.scheduleMain = null;
			this.initializeNewScheduleCreation();
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},
		onIsAllDayTaskChange ($event, indexSchedule) {
			this.scheduleMain.scheduleOccurrencesCreateDto[indexSchedule].occurrenceDuration = $event ? 1439 : 60;
			this.isAllDayTask = $event;
		},

		goToday () {
			const base = new Date();
			this.currentDate = base.toISOString().substr(0, 10);
			this.currentMonth = new Date(base).getMonth() + 1;
			this.currentYear = new Date(base).getFullYear();
		},
	},
	watch: {
		// schedule
		'scheduleMain.scheduleOccurrencesCreateDto': {
			handler (newVal) {
				if (!newVal) return;
				newVal.forEach((taskOccurrence) => {
					try {
						// Build the RRule configuration from your object
						// If no byHour/byMinute are provided but a defaultTime exists, use it as a fallback
						let byhour = taskOccurrence.byHour && taskOccurrence.byHour.length ? taskOccurrence.byHour : [];
						let byminute = taskOccurrence.byMinute && taskOccurrence.byMinute.length ? taskOccurrence.byMinute : [];
						// If hours are provided but minutes are not, default minutes to 0 for preview/building RRule
						if (byhour.length && !byminute.length) {
							byminute = [0];
						}
						if (!byhour.length && !byminute.length && taskOccurrence.defaultTime) {
							const parts = taskOccurrence.defaultTime.split(':').map((p) => Number(p));
							const h = Number.isFinite(parts[0]) ? parts[0] : null;
							const m = parts.length > 1 && Number.isFinite(parts[1]) ? parts[1] : 0;
							if (h !== null && !Number.isNaN(h)) byhour = [h];
							if (!Number.isNaN(m)) byminute = [m];
						}

						const rruleConfig = {
							dtstart: new Date(taskOccurrence.occurrenceStartAt),
							...(taskOccurrence.occurrenceEndAt && { until: new Date(taskOccurrence.occurrenceEndAt) }),
							...(taskOccurrence.frequency && { freq: taskOccurrence.frequency }),
							...(taskOccurrence.interval && { interval: taskOccurrence.interval }),
							...(taskOccurrence.count && { count: taskOccurrence.count }),
							...(taskOccurrence.byWeekDay?.length && { byweekday: taskOccurrence.byWeekDay }),
							...(taskOccurrence.byMonth?.length && { bymonth: taskOccurrence.byMonth }),
							...(taskOccurrence.byMonthDay?.length && { bymonthday: taskOccurrence.byMonthDay }),
							...(taskOccurrence.byYearDay?.length && { byyearday: taskOccurrence.byYearDay }),
							...(taskOccurrence.byWeekNumber?.length && { byweekno: taskOccurrence.byWeekNumber }),
							...(byhour.length && { byhour }),
							...(byminute.length && { byminute }),
							wkst: taskOccurrence.startDayOfWeek?.weekday ?? RRule.MO,
						};
						// Create the rule
						const rrule = new RRule(rruleConfig);

						// Assign the generated rule string
						taskOccurrence.rruleString = rrule.toString();
					} catch (err) {
						taskOccurrence.rruleString = '';
					}
				});
			},
			deep: true,
			immediate: false,
		},
		scheduleInfo: {
			handler (newVal, oldVal) {
				this.manageCreationStartEndDatePerMonth(newVal);
				this.scheduleInfo.scheduleByDateOrMonth = this.scheduleInfo.dateCreationOption === 0 ? 'month' : 'custom';
			},
			deep: true, // required to watch nested changes
			immediate: false, // optional, run immediately on mount if true
		},
		scheduleMain (newVal) {
			if (newVal === null) {
				this.$store.dispatch('schedules/getSchedulesByOwnerId', this.authenticated.id);
			}
		},
		async '$store.state.detailsDrawer' (newVal) {
			if (newVal) {
				this.monthOptions = this.months();
				await this.getHospitals();
				await this.initializeNewScheduleCreation();
			}
		},
	},
};
</script>
<style scoped>
.v-dialog__content {
	z-index: 2001 !important;
}
.primary2Bg {
	background-color: #a41717 !important;
}
.primaryBg {
	background-color: #ff585f !important;
}
</style>
